import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Loader2, Orbit, RotateCcw, Search, SlidersHorizontal, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import * as Auth from "@/contexts/AuthContext";
import {
  BEREIK, PALET, PERSONENKNOPPEN, SOORTEN, SOORT_TEKST, STANDAARD, adresVan, bewaarInstellingen, buren, clusters,
  NAAM_RAND, geldig, geraakt, gefilterd, groepen, klaar, kleurVan, krachten, leesInstellingen, opslagSleutel,
  maakIndeling, naamPlekken, naamVak, passend, plaatsNamen, plekken, samengevoegd, schermStraal, stap, straal, verwarm, wilNaam, zoek,
} from "@/lib/universum";

/**
 * Het Universum (post U deel 2, Frank 23 oktober 2026; GL198-U 24 oktober): een venster met de
 * grafiek van alle verbindingen, naar het voorbeeld van de graafweergave van Obsidian. Geopend
 * met de knop naast de klok van de Time Machine. De rekenkant staat in `lib/universum.js`; hier
 * wordt alleen getekend en bediend.
 *
 * Een canvas en geen svg: met duizenden stippen is een DOM-element per stip te traag, ook in de
 * PWA. EEN LUS PER BEELD: zolang de indeling beweegt rekent hij hooguit 12 ms per beeld, en hij
 * tekent alleen als er iets veranderd is. Een schuifregelaar wijzigt dus niets aan React-werk
 * per stip; hij zet een waarde en het volgende beeld toont hem.
 */

/**
 * Wie er is ingelogd, voor de instellingen per gebruiker. `useLidId` gooit nooit, ook niet
 * buiten de provider; een omgeving die alleen `useAuth` levert, krijgt die. EENMAAL gekozen,
 * op moduleniveau, zodat de volgorde van hooks per weergave vastligt.
 */
const useLid = Auth.useLidId || (() => Auth.useAuth?.()?.user?.id ?? null);

const HALO = { donker: "rgba(18, 14, 12, 0.85)", licht: "rgba(255, 252, 248, 0.9)" };
const LETTER = 11;

function opslag() {
  try { return window.localStorage; } catch { return null; }
}

/**
 * De instellingen bij het openen. Wie nog niets heeft bewaard en een smal scherm heeft, begint
 * met het paneel dicht: op 390 punten bedekte het de hele grafiek (gemeten 24 oktober).
 */
function beginInstellingen(lid) {
  const inst = leesInstellingen(opslag(), lid);
  let bewaard = null;
  try { bewaard = opslag()?.getItem(opslagSleutel(lid)); } catch { /* geen opslag */ }
  const smal = typeof window !== "undefined" && window.innerWidth < 700;
  return !bewaard && smal ? { ...inst, paneel: false } : inst;
}

function Blok({ titel, open, wissel, children, testid }) {
  return (
    <section className="border-t pt-2 first:border-t-0 first:pt-0" data-testid={testid}>
      <button type="button" onClick={wissel} aria-expanded={open}
              className="w-full flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
              data-testid={`${testid}-kop`}>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{titel}
      </button>
      {open && <div className="mt-2 space-y-2">{children}</div>}
    </section>
  );
}

function Schakelaar({ label, aan, zet, testid }) {
  return (
    <label className="flex items-center justify-between gap-2 text-sm">
      <span>{label}</span>
      <Switch checked={aan} onCheckedChange={zet} data-testid={testid} aria-label={label} />
    </label>
  );
}

function Regelaar({ label, sleutel, waarde, zet }) {
  const [lo, hi, st] = BEREIK[sleutel];
  return (
    <label className="block text-sm">
      <span className="flex justify-between">
        <span>{label}</span>
        <span className="tabular-nums text-xs text-muted-foreground">{Number(waarde).toFixed(st < 1 ? 2 : 0)}</span>
      </span>
      <input type="range" min={lo} max={hi} step={st} value={waarde} aria-label={label}
             onChange={(e) => zet(sleutel, Number(e.target.value))}
             className="w-full h-4 accent-[hsl(var(--primary))]" data-testid={`universum-${sleutel}`} />
    </label>
  );
}

export default function UniversumVenster({ open, onOpenChange }) {
  const { t } = useTaal();
  const navigate = useNavigate();
  const lid = useLid();
  const [graaf, setGraaf] = useState(null);
  const [fout, setFout] = useState(false);
  // DE INSTELLINGEN PER GEBRUIKER, bewaard onder zijn eigen sleutel.
  const [inst, setInst] = useState(() => beginInstellingen(lid));
  const [groep, setGroep] = useState("");
  const [term, setTerm] = useState("");
  const [gekozen, setGekozen] = useState(null);
  // Deel 3: personen uit Fathom, pas op de knop; openen kost zo geen netwerkaanroep.
  // Sinds 24 okt per bron (Fathom, mail, agenda, TASQ), elk met een eigen knop.
  const [personen, setPersonen] = useState({});
  const [personenMelding, setPersonenMelding] = useState("");
  // Deel 4: verwijzingen tussen documenten, ook pas op de knop.
  const [verwijzingen, setVerwijzingen] = useState("uit");
  const canvasRef = useRef(null);
  const staat = useRef({
    ind: null, stippen: [], schaal: 1, tx: 0, ty: 0, wijzers: new Map(), versleept: 0,
    zelfBewogen: false, hover: -1, vuil: true, kleuren: null, breedte: new Map(),
  });
  const teken = useRef(() => {});
  const instRef = useRef(inst);
  instRef.current = inst;

  useEffect(() => { setInst(beginInstellingen(lid)); }, [lid]);
  useEffect(() => {
    const id = setTimeout(() => bewaarInstellingen(opslag(), lid, inst), 250);
    return () => clearTimeout(id);
  }, [inst, lid]);
  const zet = useCallback((sleutel, waarde) => setInst((oud) => geldig({ ...oud, [sleutel]: waarde })), []);

  useEffect(() => {
    if (!open) return;
    let weg = false;
    setFout(false);
    axios.get(`${API}/universum`)
      .then((r) => { if (!weg) setGraaf(r.data); })
      .catch(() => { if (!weg) setFout(true); });
    return () => { weg = true; };
  }, [open]);

  const soorten = useMemo(() => new Set(inst.soorten), [inst.soorten]);
  const zicht = useMemo(() => gefilterd(graaf, {
    soorten, groep, alleenBestaand: inst.alleenBestaand, losse: inst.losse,
  }), [graaf, soorten, groep, inst.alleenBestaand, inst.losse]);
  const cluster = useMemo(() => clusters(graaf), [graaf]);
  const gevonden = useMemo(() => new Set(zoek(zicht.stippen, term)), [zicht, term]);
  const licht = useMemo(() => (gekozen ? buren(zicht.lijnen, gekozen) : null), [zicht, gekozen]);

  // DE TEKENING.
  teken.current = () => {
    const canvas = canvasRef.current;
    const st = staat.current;
    // Zonder 2d-tekenvlak (jsdom) wordt het niet elk beeld opnieuw gevraagd.
    if (!canvas || st.geenTekenvlak) return;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) { st.geenTekenvlak = true; return; }
    const { ind, schaal, tx, ty } = st;
    if (!ind) return;
    const opties = instRef.current;
    const isLicht = document.documentElement.classList.contains("light");
    const palet = isLicht ? PALET.licht : PALET.donker;
    const stippen = st.stippen;
    const dpr = window.devicePixelRatio || 1;
    const b = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(b * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(b * dpr); canvas.height = Math.round(h * dpr);
    }
    // Kleuren en volgorde per kleur, een keer per selectie en palet.
    if (!st.kleuren || st.kleuren.stippen !== stippen || st.kleuren.licht !== isLicht) {
      const per = new Map();
      stippen.forEach((s, i) => {
        const k = kleurVan(s, cluster.get(s.id), isLicht);
        if (!per.has(k)) per.set(k, []);
        per.get(k).push(i);
      });
      st.kleuren = { stippen, licht: isLicht, per: [...per.entries()] };
    }
    const groot = opties.stipgrootte;
    const r = (i) => schermStraal(straal(stippen[i].graad, groot), schaal) / schaal;
    const focus = gekozen ? stippen.findIndex((s) => s.id === gekozen) : st.hover;
    const focusId = focus >= 0 ? stippen[focus]?.id : null;
    const rond = focusId === gekozen ? licht : (focusId ? buren(zicht.lijnen, focusId) : null);
    const dof = (i) => focusId && stippen[i].id !== focusId && !rond?.has(stippen[i].id);
    // Wat in beeld is, in wereldcoordinaten, met een rand voor namen en stippen op de grens.
    const wx0 = (-b / 2 - tx) / schaal - 20; const wx1 = (b / 2 - tx) / schaal + 20;
    const wy0 = (-h / 2 - ty) / schaal - 20; const wy1 = (h / 2 - ty) / schaal + 20;
    const inBeeld = (i) => ind.x[i] >= wx0 && ind.x[i] <= wx1 && ind.y[i] >= wy0 && ind.y[i] <= wy1;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, b, h);
    ctx.translate(b / 2 + tx, h / 2 + ty);
    ctx.scale(schaal, schaal);

    // LIJNEN: dun en altijd even dik op het scherm. Met een focus dimt de rest.
    const dik = (0.8 * opties.lijndikte) / schaal;
    const lijnPad = (alleenFocus) => {
      ctx.beginPath();
      for (let p = 0; p < ind.paren.length; p += 2) {
        const a = ind.paren[p];
        const c = ind.paren[p + 1];
        const raakt = focus === a || focus === c;
        if (alleenFocus !== raakt) continue;
        ctx.moveTo(ind.x[a], ind.y[a]);
        ctx.lineTo(ind.x[c], ind.y[c]);
      }
    };
    ctx.lineWidth = dik;
    ctx.strokeStyle = focusId ? palet.lijnDof : palet.lijn;
    lijnPad(false);
    ctx.stroke();
    if (focusId) {
      ctx.lineWidth = dik * 2;
      ctx.strokeStyle = palet.lijnFel;
      lijnPad(true);
      ctx.stroke();
    }
    // PIJLEN, van `van` naar `naar`, met de punt aan de rand van de doelstip.
    if (opties.pijlen) {
      const lengte = (5 * Math.max(0.6, opties.lijndikte)) / schaal;
      ctx.fillStyle = focusId ? palet.lijnFel : palet.lijn;
      ctx.beginPath();
      for (let p = 0; p < ind.paren.length; p += 2) {
        const a = ind.paren[p];
        const c = ind.paren[p + 1];
        if (focusId && focus !== a && focus !== c) continue;
        if (!inBeeld(c)) continue;
        const dx = ind.x[c] - ind.x[a];
        const dy = ind.y[c] - ind.y[a];
        const d = Math.hypot(dx, dy) || 1;
        const ux = dx / d; const uy = dy / d;
        const px = ind.x[c] - ux * r(c); const py = ind.y[c] - uy * r(c);
        ctx.moveTo(px, py);
        ctx.lineTo(px - ux * lengte - uy * lengte * 0.5, py - uy * lengte + ux * lengte * 0.5);
        ctx.lineTo(px - ux * lengte + uy * lengte * 0.5, py - uy * lengte - ux * lengte * 0.5);
        ctx.closePath();
      }
      ctx.fill();
    }
    // STIPPEN, per kleur in een pad. Gedimde stippen eerst en doorzichtig.
    for (const fase of focusId ? [true, false] : [false]) {
      ctx.globalAlpha = fase ? 0.15 : 1;
      for (const [kleur, lijst] of st.kleuren.per) {
        ctx.fillStyle = kleur;
        ctx.beginPath();
        for (const i of lijst) {
          if ((focusId ? dof(i) : false) !== fase || !inBeeld(i)) continue;
          const ri = r(i);
          ctx.moveTo(ind.x[i] + ri, ind.y[i]);
          ctx.arc(ind.x[i], ind.y[i], ri, 0, Math.PI * 2);
        }
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    ctx.lineWidth = 1.5 / schaal;
    ctx.strokeStyle = palet.rand;
    ctx.beginPath();
    for (let i = 0; i < ind.n; i += 1) {
      if (i !== focus && !gevonden.has(stippen[i].id)) continue;
      ctx.moveTo(ind.x[i] + r(i) + 1.5 / schaal, ind.y[i]);
      ctx.arc(ind.x[i], ind.y[i], r(i) + 1.5 / schaal, 0, Math.PI * 2);
    }
    ctx.stroke();

    // NAMEN, op het scherm en nooit over elkaar: eerst de belangrijkste, de rest wijkt of
    // schuift naar een andere kant van zijn stip (GL199-1). De breedte is GEMETEN.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = `${LETTER}px system-ui, sans-serif`;
    const kandidaten = [];
    for (let i = 0; i < ind.n; i += 1) {
      if (!inBeeld(i) || dof(i)) continue;
      const s = stippen[i];
      const sr = r(i) * schaal;
      const bijzonder = i === focus || gevonden.has(s.id) || (focusId && rond?.has(s.id));
      if (!wilNaam(opties, straal(s.graad, groot), schaal, bijzonder)) continue;
      const naam = String(s.naam || "").slice(0, 40);
      let breed = st.breedte.get(naam);
      if (breed === undefined) { breed = ctx.measureText(naam).width; st.breedte.set(naam, breed); }
      const vak = naamVak(breed, LETTER);
      const [eerst, ...alt] = naamPlekken(b / 2 + tx + ind.x[i] * schaal, h / 2 + ty + ind.y[i] * schaal, sr, vak.b, vak.h);
      kandidaten.push({ i, naam, prio: (bijzonder ? 1000 : 0) + sr, ...eerst, ...vak, alt: bijzonder ? alt : undefined });
    }
    kandidaten.sort((p, q) => q.prio - p.prio);
    const plek = new Map(kandidaten.map((k) => [k.i, k]));
    ctx.textBaseline = "top";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = isLicht ? HALO.licht : HALO.donker;
    ctx.fillStyle = palet.naam;
    for (const p of plaatsNamen(kandidaten)) {
      const k = plek.get(p.i);
      ctx.strokeText(k.naam, p.x + NAAM_RAND, p.y + NAAM_RAND);
      ctx.fillText(k.naam, p.x + NAAM_RAND, p.y + NAAM_RAND);
    }
    st.namen = kandidaten.length;
  };

  // DE INDELING, opnieuw bij een andere selectie. Stippen die er al waren houden hun plek.
  useEffect(() => {
    if (!open) return;
    const st = staat.current;
    const vorige = plekken(st.ind, st.stippen);
    st.ind = maakIndeling(zicht.stippen, zicht.lijnen, vorige);
    st.stippen = zicht.stippen;
    st.hover = -1;
    st.vuil = true;
  }, [open, zicht]);

  // EEN LUS PER BEELD, zolang het venster open is.
  useEffect(() => {
    if (!open) return undefined;
    let frame;
    const lus = () => {
      const st = staat.current;
      const ind = st.ind;
      if (ind && !klaar(ind)) {
        const k = krachten(instRef.current);
        const begin = performance.now();
        do { stap(ind, k); } while (!klaar(ind) && performance.now() - begin < 12);
        const canvas = canvasRef.current;
        if (!st.zelfBewogen && canvas?.clientWidth) {
          // Op een breed scherm past het geheel naast het paneel; op de telefoon ligt het eroverheen.
          const rechts = instRef.current.paneel && canvas.clientWidth >= 700 ? 300 : 0;
          Object.assign(st, passend(ind, canvas.clientWidth, canvas.clientHeight, 0.9, rechts));
        }
        st.vuil = true;
      }
      if (st.vuil) { st.vuil = false; teken.current(); }
      frame = requestAnimationFrame(lus);
    };
    frame = requestAnimationFrame(lus);
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // Een kracht verschuift: de indeling komt weer in beweging, vanaf waar hij is.
  const eerste = useRef(true);
  useEffect(() => {
    if (eerste.current) { eerste.current = false; return; }
    if (staat.current.ind) verwarm(staat.current.ind, 0.3);
  }, [inst.centraal, inst.afstoting, inst.lijnkracht, inst.lijnafstand]);

  // Het paneel gaat open of dicht: wie nog niet zelf heeft geschoven of gezoomd, krijgt het
  // geheel opnieuw passend in de ruimte die er nu is.
  useEffect(() => {
    const st = staat.current;
    const canvas = canvasRef.current;
    if (!st.ind || st.zelfBewogen || !canvas?.clientWidth) return;
    const rechts = inst.paneel && canvas.clientWidth >= 700 ? 300 : 0;
    Object.assign(st, passend(st.ind, canvas.clientWidth, canvas.clientHeight, 0.9, rechts));
    st.vuil = true;
  }, [inst.paneel]);

  // Al het andere hoeft alleen opnieuw getekend.
  useEffect(() => { staat.current.vuil = true; }, [inst, gekozen, gevonden, licht]);

  const wereld = useCallback((cx, cy) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const { schaal, tx, ty } = staat.current;
    return [(cx - rect.left - rect.width / 2 - tx) / schaal, (cy - rect.top - rect.height / 2 - ty) / schaal];
  }, []);

  const stralen = useCallback(() => {
    const st = staat.current;
    return Float32Array.from(st.stippen.map((s) => schermStraal(straal(s.graad, instRef.current.stipgrootte), st.schaal) / st.schaal));
  }, []);

  const omlaag = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    staat.current.wijzers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    staat.current.versleept = 0;
  };
  const beweeg = (e) => {
    const st = staat.current;
    const vorig = st.wijzers.get(e.pointerId);
    if (!vorig) {
      // AANWIJZEN met de muis: de naam en de lijnen van de stip eronder.
      if (e.pointerType === "touch" || !st.ind) return;
      const [wx, wy] = wereld(e.clientX, e.clientY);
      const i = geraakt(st.ind, stralen(), wx, wy, 3 / st.schaal);
      if (i !== st.hover) { st.hover = i; st.vuil = true; }
      return;
    }
    if (st.wijzers.size === 2) {
      // KNIJPEN OM IN TE ZOOMEN, op de telefoon.
      const [a, c] = [...st.wijzers.values()];
      const voor = Math.hypot(a.x - c.x, a.y - c.y);
      st.wijzers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const [d, f] = [...st.wijzers.values()];
      const na = Math.hypot(d.x - f.x, d.y - f.y);
      if (voor > 0) st.schaal = Math.min(12, Math.max(0.03, st.schaal * (na / voor)));
    } else {
      st.tx += e.clientX - vorig.x;
      st.ty += e.clientY - vorig.y;
      st.wijzers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    st.versleept += Math.abs(e.clientX - vorig.x) + Math.abs(e.clientY - vorig.y);
    if (st.versleept >= 5) st.zelfBewogen = true;
    st.vuil = true;
  };
  const omhoog = (e) => {
    const st = staat.current;
    st.wijzers.delete(e.pointerId);
    if (st.versleept < 5 && st.ind) {
      const [wx, wy] = wereld(e.clientX, e.clientY);
      const i = geraakt(st.ind, stralen(), wx, wy, 4 / st.schaal);
      setGekozen(i >= 0 ? st.stippen[i].id : null);
    }
  };
  const wiel = (e) => {
    const st = staat.current;
    const factor = Math.exp(-e.deltaY * 0.0015);
    const nieuw = Math.min(12, Math.max(0.03, st.schaal * factor));
    const [wx, wy] = wereld(e.clientX, e.clientY);
    st.tx -= wx * (nieuw - st.schaal);
    st.ty -= wy * (nieuw - st.schaal);
    st.schaal = nieuw;
    st.zelfBewogen = true;
    st.vuil = true;
  };

  // ZOEKEN: de eerste treffer wordt gekozen en in het midden gezet.
  const zoekEnGa = (waarde) => {
    setTerm(waarde);
    const eerste = zoek(zicht.stippen, waarde)[0];
    const st = staat.current;
    if (!eerste || !st.ind) return;
    const i = st.stippen.findIndex((s) => s.id === eerste);
    if (i >= 0) {
      const canvas = canvasRef.current;
      const rechts = inst.paneel && canvas?.clientWidth >= 700 ? 300 : 0;
      st.tx = -st.ind.x[i] * st.schaal - rechts / 2;
      st.ty = -st.ind.y[i] * st.schaal;
      st.zelfBewogen = true;
    }
    setGekozen(eerste);
  };

  // ANIMEER: de indeling opnieuw vanaf het begin, zodat je het universum ziet ontstaan.
  const animeer = () => {
    const st = staat.current;
    st.ind = maakIndeling(zicht.stippen, zicht.lijnen);
    st.stippen = zicht.stippen;
    st.zelfBewogen = false;
    st.vuil = true;
  };

  const haalPersonen = (bron) => {
    setPersonen((p) => ({ ...p, [bron]: "bezig" }));
    const adres = bron === "fathom" ? `${API}/universum/personen` : `${API}/universum/personen?bron=${bron}`;
    axios.get(adres)
      .then((r) => {
        setPersonenMelding(r.data?.melding || "");
        setGraaf((g) => samengevoegd(g, r.data));
        setPersonen((p) => ({ ...p, [bron]: "aan" }));
      })
      .catch(() => { setPersonenMelding(t("Personen konden niet geladen worden.")); setPersonen((p) => ({ ...p, [bron]: "uit" })); });
  };

  const haalVerwijzingen = () => {
    setVerwijzingen("bezig");
    axios.get(`${API}/universum/verwijzingen`)
      .then((r) => { setGraaf((g) => samengevoegd(g, r.data)); setVerwijzingen("aan"); })
      .catch(() => { setPersonenMelding(t("Verwijzingen konden niet geladen worden.")); setVerwijzingen("uit"); });
  };

  const openStip = (s) => {
    const adres = adresVan(s);
    if (!adres) return;
    if (/^https:\/\//.test(adres)) { window.open(adres, "_blank", "noopener"); return; }
    navigate(adres);
    onOpenChange?.(false);
  };

  const wisselSoort = (soort) => setInst((oud) => {
    const aan = new Set(oud.soorten);
    if (aan.has(soort)) aan.delete(soort); else aan.add(soort);
    return geldig({ ...oud, soorten: [...aan] });
  });

  const stip = gekozen ? zicht.stippen.find((s) => s.id === gekozen) : null;
  const afgekapt = Object.entries(graaf?.afgekapt || {});
  const isLichtNu = typeof document !== "undefined" && document.documentElement.classList.contains("light");
  const legenda = isLichtNu ? PALET.licht.soort : PALET.donker.soort;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1200px] w-[96vw] h-[88vh] p-0 gap-0 flex flex-col" data-testid="universum">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="font-serif text-lg flex items-center gap-2">
            <Orbit className="h-4 w-4 text-[hsl(var(--accent))]" />{t("Universum")}
          </DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-2 flex flex-wrap items-center gap-2">
          <select value={groep} onChange={(e) => { setGroep(e.target.value); setGekozen(null); }}
                  className="h-8 text-sm border rounded px-2 bg-background max-w-[12rem]" aria-label={t("Team of project")}
                  data-testid="universum-groep">
            <option value="">{t("Alle teams en projecten")}</option>
            {groepen(graaf).map((g) => <option key={g.id} value={g.id}>{g.naam}</option>)}
          </select>
          {PERSONENKNOPPEN.map((k) => (
            <Button key={k.bron} type="button" variant="outline" size="sm" className="h-8 text-xs"
                    disabled={(personen[k.bron] || "uit") !== "uit" || !graaf} onClick={() => haalPersonen(k.bron)}
                    data-testid={k.testid}>
              {personen[k.bron] === "bezig" ? t("Personen laden") : t(k.tekst)}
            </Button>
          ))}
          <Button type="button" variant="outline" size="sm" className="h-8 text-xs"
                  disabled={verwijzingen !== "uit" || !graaf} onClick={haalVerwijzingen} data-testid="universum-verwijzingen">
            {verwijzingen === "bezig" ? t("Verwijzingen laden") : t("Verwijzingen tussen documenten")}
          </Button>
          {personenMelding && (
            <span className="text-xs text-[hsl(var(--accent))]" data-testid="universum-personen-melding">{personenMelding}</span>
          )}
        </div>
        {/* Een DICHTE achtergrond: het dialoogvenster is glas (wit op 0,72 over het donkere
            scherm), en daarop oogde de lichte modus grijs (gemeten 24 oktober). */}
        <div className="relative flex-1 min-h-0 border-t overflow-hidden bg-background rounded-b-lg">
          {!graaf && !fout && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin" aria-label={t("Universum laden")} />
            </div>
          )}
          {fout && (
            <p className="p-4 text-sm text-[hsl(var(--accent))]" data-testid="universum-fout">
              {t("Het universum kon niet geladen worden.")}
            </p>
          )}
          {graaf && zicht.stippen.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground" data-testid="universum-leeg">
              {t("Er is nog niets om te tonen.")}
            </p>
          )}
          <canvas ref={canvasRef} className="w-full h-full touch-none cursor-grab"
                  onPointerDown={omlaag} onPointerMove={beweeg} onPointerUp={omhoog} onPointerCancel={omhoog}
                  onPointerLeave={() => { if (staat.current.hover >= 0) { staat.current.hover = -1; staat.current.vuil = true; } }}
                  onWheel={wiel} data-testid="universum-canvas" aria-label={t("Universum")} />
          <p className="absolute left-3 bottom-2 text-[11px] text-muted-foreground pointer-events-none" data-testid="universum-telling">
            {t("{s} stippen, {l} verbindingen", { s: zicht.stippen.length, l: zicht.lijnen.length })}
            {afgekapt.length > 0 && ` - ${t("ingekort")}: ${afgekapt.map(([k, v]) => `${t(SOORT_TEKST[k] || k)} ${v}`).join(", ")}`}
          </p>
          {stip && (
            <div className="absolute left-3 top-3 w-64 max-w-[calc(100%-1.5rem)] rounded-lg border bg-background/95 p-3 space-y-2 shadow"
                 data-testid="universum-stip">
              <p className="text-sm font-medium break-words">{stip.naam}</p>
              <p className="text-xs text-muted-foreground">
                {stip.bestaat === false
                  ? t("Een verwijzing naar een bestand dat er niet is.")
                  : `${t(SOORT_TEKST[stip.soort])} - ${t("{n} verbindingen", { n: stip.graad })}`}
              </p>
              {adresVan(stip) && (
                <Button type="button" size="sm" className="h-7 text-xs" data-testid="universum-open"
                        onClick={() => openStip(stip)}>
                  {t("Openen")}
                </Button>
              )}
            </div>
          )}
          <Button type="button" variant="outline" size="icon" className="absolute right-3 top-3 h-8 w-8 bg-background/90"
                  onClick={() => zet("paneel", !inst.paneel)} aria-expanded={inst.paneel}
                  aria-label={inst.paneel ? t("Instellingen verbergen") : t("Instellingen tonen")}
                  data-testid="universum-paneel-knop">
            {inst.paneel ? <X className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />}
          </Button>
          {inst.paneel && (
            <aside className="absolute right-3 top-14 bottom-3 w-72 max-w-[calc(100%-1.5rem)] overflow-y-auto rounded-lg border bg-background/95 p-3 space-y-3 shadow"
                   data-testid="universum-paneel" aria-label={t("Instellingen van het universum")}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{t("Instellingen")}</span>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1"
                        onClick={() => setInst(geldig({ ...STANDAARD, paneel: true }))} data-testid="universum-standaard">
                  <RotateCcw className="h-3.5 w-3.5" />{t("Standaard")}
                </Button>
              </div>
              <Blok titel={t("Filters")} open={inst.blokFilters} wissel={() => zet("blokFilters", !inst.blokFilters)}
                    testid="universum-blok-filters">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
                  <Input value={term} onChange={(e) => zoekEnGa(e.target.value)} placeholder={t("Zoek in het universum")}
                         className="h-8 pl-7 text-sm" data-testid="universum-zoek" />
                </div>
                {term && (
                  <p className="text-xs text-muted-foreground" data-testid="universum-gevonden">
                    {t("{n} gevonden", { n: gevonden.size })}
                  </p>
                )}
                <Schakelaar label={t("Labels")} aan={inst.labels} zet={(v) => zet("labels", v)} testid="universum-labels" />
                <Schakelaar label={t("Documenten")} aan={soorten.has("document")} zet={() => wisselSoort("document")}
                            testid="universum-documenten" />
                <Schakelaar label={t("Alleen bestaande bestanden")} aan={inst.alleenBestaand}
                            zet={(v) => zet("alleenBestaand", v)} testid="universum-bestaand" />
                <Schakelaar label={t("Losse stippen")} aan={inst.losse} zet={(v) => zet("losse", v)} testid="universum-losse" />
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="universum-legenda">
                  {SOORTEN.map((s) => (
                    <button key={s} type="button" onClick={() => wisselSoort(s)} data-testid={`universum-soort-${s}`}
                            aria-pressed={soorten.has(s)}
                            className={`text-xs rounded-full border px-2 py-0.5 flex items-center gap-1.5 ${soorten.has(s) ? "" : "opacity-40"}`}>
                      <span className="h-2 w-2 rounded-full inline-block" style={{ background: legenda[s] }} />
                      {t(SOORT_TEKST[s])}
                    </button>
                  ))}
                </div>
              </Blok>
              <Blok titel={t("Weergave")} open={inst.blokWeergave} wissel={() => zet("blokWeergave", !inst.blokWeergave)}
                    testid="universum-blok-weergave">
                <Schakelaar label={t("Pijlen")} aan={inst.pijlen} zet={(v) => zet("pijlen", v)} testid="universum-pijlen" />
                <Regelaar label={t("Drempel voor namen")} sleutel="drempel" waarde={inst.drempel} zet={zet} />
                <Regelaar label={t("Stipgrootte")} sleutel="stipgrootte" waarde={inst.stipgrootte} zet={zet} />
                <Regelaar label={t("Lijndikte")} sleutel="lijndikte" waarde={inst.lijndikte} zet={zet} />
                <Button type="button" variant="outline" size="sm" className="h-8 w-full text-xs" onClick={animeer}
                        data-testid="universum-animeer">
                  {t("Animeer")}
                </Button>
              </Blok>
              <Blok titel={t("Krachten")} open={inst.blokKrachten} wissel={() => zet("blokKrachten", !inst.blokKrachten)}
                    testid="universum-blok-krachten">
                <Regelaar label={t("Centrale kracht")} sleutel="centraal" waarde={inst.centraal} zet={zet} />
                <Regelaar label={t("Afstotende kracht")} sleutel="afstoting" waarde={inst.afstoting} zet={zet} />
                <Regelaar label={t("Lijnkracht")} sleutel="lijnkracht" waarde={inst.lijnkracht} zet={zet} />
                <Regelaar label={t("Lijnafstand")} sleutel="lijnafstand" waarde={inst.lijnafstand} zet={zet} />
              </Blok>
            </aside>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
